import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { change, reset } from 'redux-form';
import { useAuth0 } from '@auth0/auth0-react';
import { Text, TentoLogo } from 'shared-components';
import { voltBaseUrl } from '@utils/platformBasedInfo';
import { flowStepComplete, flowError } from '@utils/gaFlow';

const HERO_IMAGE_SRC = 'https://media.tento.co/photos/bcm-landing-hero.jpg';
const TENTO_SYMBOL_SRC = 'https://media.tento.co/photos/tento-symbol.svg';
const TENTO_INSTAGRAM_URL = 'https://www.instagram.com/tento_co/';
const TENTO_LINKEDIN_URL = 'https://www.linkedin.com/company/tento-co/';
const TENTO_MARKETING_URL = 'https://www.tento.com';

type EmailFormVariant = 'light' | 'dark';

const EmailCaptureForm: React.FC<{ variant?: EmailFormVariant; className?: string }> = ({
  variant = 'light',
  className = '',
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isAuthenticated, user, logout } = useAuth0();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<React.ReactNode | null>(null);

  const handleSignOut = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    logout({ logoutParams: { returnTo: window.location.origin + '/bcm' } });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!isValid) {
      flowError('apply_bcm', 'landing', 'validation', 'invalid_email');
      setError('Please enter a valid email address.');
      return;
    }

    // Edge case: user is already authenticated with a different email. The downstream
    // /bcm/app/info page disables the email field when authenticated, and Auth0LoggedInSetState
    // unconditionally overwrites owner_1_email with the Auth0 idToken email — so proceeding
    // here would silently use the logged-in email instead of what the user just typed.
    const loggedInEmail = user?.email?.toLowerCase();
    const typedEmail = trimmed.toLowerCase();
    if (isAuthenticated && loggedInEmail && loggedInEmail !== typedEmail) {
      setError(
        <>
          You&apos;re already signed in as <strong>{loggedInEmail}</strong>. Continue with that email, or{' '}
          <button
            type="button"
            onClick={handleSignOut}
            className="underline font-semibold cursor-pointer bg-transparent border-0 p-0 text-inherit"
          >
            sign out
          </button>{' '}
          to use a different one.
        </>
      );
      return;
    }

    flowStepComplete('apply_bcm', 'landing');
    dispatch(reset('application'));
    dispatch(change('application', 'application_type', 'bcm-short'));
    dispatch(change('application', 'owner_1_email', trimmed));
    history.push('/bcm/app/info');
  };

  const inputBg = variant === 'dark' ? 'bg-[#f2eae4]' : 'bg-[#fff6f0]';

  return (
    <form
      onSubmit={handleSubmit}
      className={`w-full max-w-[380px] flex flex-col gap-[8px] ${className}`}
      aria-label="Get free business credit score"
    >
      <div className="flex items-center gap-[5px] w-full">
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          className={`flex-1 min-w-0 h-[45px] ${inputBg} border border-[#d5cec8] rounded-[5px] px-[20px] py-[15px] font-standard text-[14px] text-[#0d0d0d] placeholder:text-[#9e9e9e] outline-none shadow-[0px_0px_10px_0px_rgba(13,13,13,0.05)] focus:border-[#0d0d0d] transition-colors`}
        />
        <button
          type="submit"
          aria-label="Submit email"
          className="shrink-0 size-[45px] bg-[#d5d973] border border-[#b9bc63] rounded-full flex items-center justify-center font-condensed text-[14px] text-[#0d0d0d] shadow-[0px_0px_10px_0px_rgba(13,13,13,0.1)] hover:bg-[#c5cd63] active:scale-95 transition-all"
        >
          →
        </button>
      </div>
      {error && (
        <Text variant="error" className="!text-[12px] text-left">
          {error}
        </Text>
      )}
    </form>
  );
};

type StepProps = { num: string; title: string; body: string; isLast?: boolean };

const HowItWorksStep: React.FC<StepProps> = ({ num, title, body, isLast = false }) => (
  <div
    className={`${
      isLast ? '' : 'border-b border-[#d5d973]'
    } pt-[20px] pb-[30px] w640:pb-[40px] grid grid-cols-[auto_1fr] w640:grid-cols-2 gap-[15px] w640:gap-[20px] items-start w-full`}
  >
    <div className="flex items-start">
      <div className="bg-[#d5d973] border border-[#b9bc63] rounded-full px-[20px] py-[5px] inline-flex items-center justify-center min-w-[58px]">
        <span className="font-extended text-[16px] text-[#0d0d0d] leading-[1.2]">{num}</span>
      </div>
    </div>
    <div className="flex flex-col gap-[10px]">
      <h3 className="font-standard text-[20px] w640:text-[26px] w1024:text-[30px] leading-[1.1] text-[#0d0d0d] text-left">
        {title}
      </h3>
      <p className="font-standard text-[15px] w640:text-[19px] w1024:text-[22.5px] leading-[1.1] text-[#0d0d0d] text-left">
        {body}
      </p>
    </div>
  </div>
);

type FooterListProps = { label: string; items: { label: string; href?: string }[] };

const FooterList: React.FC<FooterListProps> = ({ label, items }) => (
  <div className="flex-1 min-w-0 flex flex-col gap-[5px] items-start">
    <div className="pb-[5px]">
      <span className="font-condensed text-[14px] uppercase text-[#9e9e9e] leading-[1.2]">{label}</span>
    </div>
    {items.map((item) => (
      <a
        key={item.label}
        href={item.href ?? '#'}
        className="font-standard text-[14px] text-[#0d0d0d] leading-[1.2] hover:underline text-left"
      >
        {item.label}
      </a>
    ))}
  </div>
);

export const BCMLandingPage: React.FC = () => {
  return (
    <div className="bg-[#f2eae4] min-h-screen p-[10px] flex flex-col gap-[10px]">
      {/* Navigation Bar */}
      <header className="bg-[#fff6f0] border border-[#d5cec8] rounded-[5px] shadow-[0px_2px_10px_0px_rgba(13,13,13,0.05)] h-[57px] px-[20px] flex items-center gap-[10px]">
        <div className="flex-1 flex items-center gap-[20px] min-w-0">
          <a
            href={TENTO_MARKETING_URL}
            aria-label="Tento Home"
            className="flex items-center"
            style={{ width: 109, height: 30 }}
          >
            <TentoLogo width="109" height="30" />
          </a>
        </div>
        <div className="flex-1 flex items-center justify-end gap-[10px] min-w-0">
          <a
            href={voltBaseUrl}
            className="bg-[#d5d973] border border-[#b9bc63] rounded-full px-[15px] py-[7.5px] font-condensed text-[14px] uppercase text-[#0d0d0d] text-center leading-[1.2] shadow-[0px_0px_10px_0px_rgba(13,13,13,0.1)] hover:bg-[#c5cd63] transition-colors"
          >
            Sign in
          </a>
        </div>
      </header>

      {/* Main Landing Card */}
      <main
        className="relative border-b border-l border-r border-[#d5cec8] rounded-[15px] shadow-[0px_0px_20px_0px_rgba(13,13,13,0.05)] overflow-hidden p-[10px] w640:p-[20px] flex flex-col gap-[20px]"
        style={{
          background: 'linear-gradient(180deg, #fff6f0 0%, #fff6f0 35%, #e8e58e 80%, #d5d973 100%)',
        }}
      >
        {/* Hero / Cover Section */}
        <section className="flex flex-col w1024:flex-row gap-[20px] items-center justify-center w-full">
          {/* Cover - Image */}
          {/* Photo capped at 45vh below the desktop breakpoint (F25, mobile UX audit 2026-10). */}
          <div className="w-full h-[min(45vh,420px)] w1024:h-auto w1024:flex-1 w1024:max-w-[726px] aspect-[726/825] flex items-center justify-center">
            <div className="relative w-full h-full rounded-[10px] overflow-hidden">
              <img
                src={HERO_IMAGE_SRC}
                alt="Hand holding a phone showing a Tento business credit score"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: '55% 55%' }}
              />
            </div>
          </div>

          {/* Cover - Information */}
          <div className="w-full w1024:flex-1 flex flex-col gap-[20px] w640:gap-[30px] items-start justify-center px-[10px] w640:px-[40px] w1024:px-[80px] py-[20px]">
            <h1 className="font-extended text-[28px] w640:text-[36px] w1024:text-[42px] leading-[1.1] text-[#0d0d0d] w-full text-left">
              Get your business credit score, for free
            </h1>
            <p className="font-standard text-[16px] leading-[1.2] text-[#0d0d0d] w-full text-left">
              Your business has its own credit profile — completely separate from your personal score. Lenders check it.
              Suppliers check it. Now you can too — no hard inquiry and no credit card required.
            </p>
            <EmailCaptureForm variant="light" />
          </div>
        </section>

        {/* How It Works */}
        <section className="border border-[#d5d973] rounded-[10px] flex flex-col items-center justify-center gap-[30px] w640:gap-[40px] py-[60px] w640:py-[80px] w1024:pt-[120px] w1024:pb-[80px] px-[20px] w640:px-[40px]">
          <div className="w-full max-w-[901.34px] flex flex-col items-center justify-center">
            <h2 className="font-extended text-[26px] w640:text-[34px] w1024:text-[42px] leading-[1.1] text-[#0d0d0d] text-center w-full">
              Three steps. Two minutes.
              <br />
              Zero impact on your score.
            </h2>
          </div>
          <div className="w-full max-w-[901.34px] flex flex-col items-center justify-center gap-[20px] py-[20px] w640:py-[40px]">
            <HowItWorksStep
              num="01"
              title="Create your free account"
              body="Just your name, email, and business EIN. No credit card, always start for free."
            />
            <HowItWorksStep
              num="02"
              title="We partner with Equifax"
              body="Soft inquiry only. We pull your credit score from the bureau most trusted by small business lenders."
            />
            <HowItWorksStep
              num="03"
              title="Your score is live"
              body="Monitor your business credit score, dig into insights driving it, go further with cash flow insights."
              isLast
            />
          </div>
        </section>

        {/* Get Started CTA */}
        <section className="bg-[#283310] rounded-[10px] flex flex-col items-center justify-center gap-[30px] w640:gap-[40px] px-[20px] w640:px-[40px] py-[60px] w640:py-[80px] w1024:py-[120px]">
          <div className="w-full max-w-[901.34px] flex flex-col gap-[20px] items-start">
            <h2 className="font-extended text-[26px] w640:text-[34px] w1024:text-[42px] leading-[1.1] text-[#fff6f0] text-center w-full">
              See what lenders see, and move confidently towards your business goals
            </h2>
            <p className="font-standard text-[16px] leading-[1.2] text-[#fff6f0] text-center w-full">
              Start for free. No hard inquiry. No credit card.
            </p>
          </div>
          <EmailCaptureForm variant="dark" />
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#f2eae4] border border-[#d5cec8] rounded-tl-[10px] rounded-tr-[10px] p-[20px] w640:p-[40px] flex flex-col w768:flex-row gap-[30px] w640:gap-[20px] items-start justify-center overflow-hidden">
        {/* Footer - Left Side */}
        <div className="flex-1 min-w-0 flex flex-col gap-[10px] h-full items-start w-full">
          <a
            href={TENTO_MARKETING_URL}
            aria-label="Tento"
            className="block"
            style={{ width: 290, maxWidth: '100%', height: 80 }}
          >
            <TentoLogo width="290" height="80" />
          </a>
          <span className="font-standard text-[14px] text-[#0d0d0d] leading-[1.2]">© Tento 2026</span>
        </div>

        {/* Footer - Center - Links */}
        <div className="flex-1 min-w-0 grid grid-cols-2 w640:grid-cols-4 gap-[10px] w-full">
          <FooterList
            label="PRODUCTS"
            items={[
              { label: 'Business Credit Scores + Insights', href: `${voltBaseUrl}/business-credit/start` },
              { label: 'Equipment Financing', href: '/eq' },
              { label: 'Working Capital', href: '/' },
            ]}
          />
          <FooterList
            label="LEARN"
            items={[
              { label: 'Wellness Resources', href: TENTO_MARKETING_URL },
              { label: 'FAQs', href: TENTO_MARKETING_URL },
              { label: 'Our Mission', href: TENTO_MARKETING_URL },
            ]}
          />
          <FooterList
            label="PARTNERS"
            items={[
              { label: 'Platform Partners', href: `${TENTO_MARKETING_URL}/for-partners` },
              { label: 'API Documentation', href: 'https://docs.tento.co/' },
            ]}
          />
          <FooterList
            label="CONNECT"
            items={[
              { label: 'LinkedIn', href: TENTO_LINKEDIN_URL },
              { label: 'Instagram', href: TENTO_INSTAGRAM_URL },
            ]}
          />
        </div>

        {/* Footer - Right Side */}
        <div className="flex flex-col h-full items-start w768:items-end justify-between gap-[20px] shrink-0">
          <div className="size-[40px]">
            <img src={TENTO_SYMBOL_SRC} alt="Tento symbol" width={40} height={40} className="block w-full h-full" />
          </div>
          <div className="flex flex-col gap-[2.5px] items-start w768:items-end justify-end">
            <a
              href={TENTO_MARKETING_URL}
              className="font-standard text-[14px] text-[#9e9e9e] leading-[1.2] hover:underline"
            >
              Terms &amp; Conditions
            </a>
            <a
              href={TENTO_MARKETING_URL}
              className="font-standard text-[14px] text-[#9e9e9e] leading-[1.2] hover:underline"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BCMLandingPage;
